import { lazy, Suspense, useRef, type Ref } from 'react';
import { useInView } from '../../hooks/use-in-view';
import { usePrefersReducedMotion } from '../../hooks/use-prefers-reduced-motion';
import { ArtColoursProvider, useArtColours, type ArtColourProps } from '../motion/art-colours';
import { CARD, CARD_BOTTOM, CUFF, HEART, SPARKLE_ART } from './hand-heart-art';
import { useLine } from './hand-heart-shared';
import { STILL_FRAME } from './hand-heart-still';

const HandHeartAnimation = lazy(() =>
	import('./hand-heart-animation').then((m) => ({ default: m.HandHeartAnimation })),
);

/** The animation's frames are most of its weight: fetched this far ahead of the card scrolling into view. */
const LOAD_AHEAD = '400px';

/** The animation's last frame, drawn with no frame data: what the server sends and reduced motion keeps. */
function HandHeartStill(props: { label: string; className?: string; bare?: boolean; ref?: Ref<SVGSVGElement> }) {
	const line = useLine();
	const colours = useArtColours();
	return (
		<svg
			ref={props.ref}
			viewBox="0 0 451 451"
			fill="none"
			role="img"
			aria-label={props.label}
			className={props.className}
		>
			{props.bare ? null : <rect width="451" height="451" rx="24" fill={colours.paper} />}
			<path d={STILL_FRAME.back} {...line} />
			{STILL_FRAME.fill ? <path d={STILL_FRAME.fill} fill={colours.paper} /> : null}
			<path d={STILL_FRAME.front} {...line} />
			<path d={`${CARD}${CARD_BOTTOM}Z`} {...line} fill={colours.paper} />
			<path d={HEART} fill={colours.accent} />
			{SPARKLE_ART.map((sparkle) => (
				<path key={sparkle.centre} d={sparkle.d} {...line} />
			))}
			<path d={CUFF} {...line} />
		</svg>
	);
}

/**
 * A fist opens and the card grows into the hand. Shows the still until the card nears the viewport, then
 * swaps in the animation, which starts from the fist once it is in view.
 *
 * How the frames are made and checked: docs/notes/portals/hand-heart-animation.md
 */
export function HandHeartCard(props: ArtColourProps & { label: string; className?: string; bare?: boolean }) {
	const ref = useRef<SVGSVGElement>(null);
	const near = useInView(ref, { margin: LOAD_AHEAD });
	const reducedMotion = usePrefersReducedMotion();
	const still = (
		<ArtColoursProvider colours={props} paper="white">
			<HandHeartStill ref={ref} label={props.label} className={props.className} bare={props.bare} />
		</ArtColoursProvider>
	);
	if (!near || reducedMotion) return still;
	return (
		<Suspense fallback={still}>
			<HandHeartAnimation {...props} />
		</Suspense>
	);
}
